import React, { useState, useRef, useEffect } from "react";
import {
  Label,
  Input,
  Form,
  Row,
  Col,
  TabPane,
  FormGroup,
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  Card,
  CardBody,
} from "reactstrap";
import { useFormik } from "formik";
import { Cropper } from "react-cropper";
import "cropperjs/dist/cropper.css";
import * as Yup from "yup";
import { getBuyerByUserId, createBuyer } from "../../../api/buyer/buyerApi";
import Swal from "sweetalert2";
import "./profile.css";
import { onAuthStateChanged } from "firebase/auth";
import { auth } from "../../ExtraPages/firebaseConfig";

// Validation schema for form fields
const validationSchema = Yup.object({
  Owner_Name: Yup.string().required("Owner name is required"),
  Employees: Yup.number()
    .typeError("Employees must be a number")
    .positive("Employees must be positive")
    .integer("Employees must be an integer"),
  Location: Yup.string().required("Location is required"),
  email: Yup.string()
    .email("Invalid email format")
    .required("Email is required"),
  Website: Yup.string().url("Invalid URL format"),
  Established: Yup.date(),
  About_Company: Yup.string(),
  Company_Location: Yup.object().shape({
    Longitude: Yup.number(),
    Latitude: Yup.number(),
  }),
});

const BuyerForm = ({ onPercentageChange }) => {
  const [profileImage, setProfileImage] = useState("");
  const [cropImage, setCropImage] = useState(null);
  const [isCropModalOpen, setIsCropModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [userId, setUserId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const cropperRef = useRef(null);

  // Fetch userId from Firebase Auth
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUserId(user ? user.uid : null);
      setIsLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // Fetch profile data when userId is available
  useEffect(() => {
    const fetchProfile = async () => {
      if (!userId) return;

      try {
        const response = await getBuyerByUserId(userId);
        console.log("Fetched buyer data:", response);

        const profile = response.data;
        const values = {
          Owner_Name: profile.Owner_Name || "",
          Employees: profile.Employees || "",
          Location: profile.Location || "",
          email: profile.email || "",
          Website: profile.Website || "",
          Established: profile.Established || "",
          About_Company: profile.About_Company || "",
          Company_Location: profile.Company_Location || {
            Longitude: "",
            Latitude: "",
          },
          profileImage: profile.profileImage || "",
        };
        formik.setValues(values);
        setProfileImage(profile.profileImage || "");

        // Calculate completion percentage
        const totalFields = Object.keys(values).length + 1; // +1 for profileImage
        const filledFields =
          Object.values(values).filter(
            (value) =>
              value &&
              (typeof value === "object"
                ? Object.values(value).some((v) => v)
                : true)
          ).length + (profileImage ? 1 : 0);
        const percentage = Math.round((filledFields / totalFields) * 100);
        onPercentageChange?.(percentage);
      } catch (err) {
        console.error("Failed to fetch profile:", err);
        Swal.fire("Error!", "Failed to fetch profile data.", "error");
      }
    };

    fetchProfile();
  }, [userId]);

  const toggleCropModal = () => setIsCropModalOpen(!isCropModalOpen);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        setCropImage(reader.result);
        toggleCropModal();
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCrop = () => {
    const cropper = cropperRef.current?.cropper;
    if (cropper) {
      const croppedCanvas = cropper.getCroppedCanvas({
        width: 300,
        height: 300,
      });
      const compressedDataUrl = croppedCanvas.toDataURL("image/jpeg", 0.7);
      setProfileImage(compressedDataUrl);
      toggleCropModal();
    }
  };

  const formik = useFormik({
    initialValues: {
      Owner_Name: "",
      Employees: "",
      Location: "",
      email: "",
      Website: "",
      Established: "",
      About_Company: "",
      Company_Location: {
        Longitude: "",
        Latitude: "",
      },
      profileImage: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!userId) {
        Swal.fire("Error!", "User not authenticated.", "error");
        return;
      }

      try {
        const payload = { ...values, userId, profileImage };
        console.log("Submitting profile data:", payload);
        await createBuyer(payload);
        setSubmitted(true);
        Swal.fire("Success!", "Profile saved successfully.", "success");

        // Recalculate completion percentage
        const totalFields = Object.keys(values).length + 1; // +1 for profileImage
        const filledFields =
          Object.values(values).filter(
            (value) =>
              value &&
              (typeof value === "object"
                ? Object.values(value).some((v) => v)
                : true)
          ).length + (profileImage ? 1 : 0);
        const percentage = Math.round((filledFields / totalFields) * 100);
        onPercentageChange?.(percentage);

        setTimeout(() => setSubmitted(false), 2000);
      } catch (error) {
        console.error("Error saving profile:", error);
        Swal.fire(
          "Error!",
          error.response?.data?.message || "Failed to save profile.",
          "error"
        );
      }
    },
  });

  const calculateCompletionPercentage = (errors, values) => {
    const totalFields = 10;
    const fieldsToCheck = [
      "Owner_Name",
      "Employees",
      "Location",
      "email",
      "Website",
      "Established",
      "About_Company",
      "Company_Location",
      "profileImage",
    ];

    let validFields =
      Object.values(values).filter(
        (value) =>
          value &&
          (typeof value === "object"
            ? Object.values(value).some((v) => v)
            : true)
      ).length + (profileImage ? 1 : 0);

    const percentage = Math.round((validFields / totalFields) * 100);
    onPercentageChange?.(percentage);
    return percentage;
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <React.Fragment>
      <TabPane tabId="2">
        <Card
          className="overview-content shadow-sm rounded-2xl w-100"
          style={{ maxWidth: "720px", border: "none", margintop: "0px" }}
        >
          <CardBody className="p-4">
            <Form onSubmit={formik.handleSubmit}>
              <Row className="mb-3 p-4" style={{ display: "none" }}>
                <Col lg={6}></Col>
                <Col lg={6} className="text-end">
                  <p className="fs-14">
                    Completion:{" "}
                    {calculateCompletionPercentage(
                      formik.errors,
                      formik.values
                    )}
                    %
                  </p>
                </Col>
              </Row>
              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="Owner_Name"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      Buyer Name
                    </Label>
                    <Input
                      name="Owner_Name"
                      type="text"
                      className={`form-control ${
                        formik.errors.Owner_Name && formik.touched.Owner_Name
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("Owner_Name")}
                    />
                    {formik.errors.Owner_Name && formik.touched.Owner_Name && (
                      <div className="invalid-feedback">
                        {formik.errors.Owner_Name}
                      </div>
                    )}
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="Employees"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      Number of Employees
                    </Label>
                    <Input
                      name="Employees"
                      type="number"
                      className={`form-control ${
                        formik.errors.Employees && formik.touched.Employees
                          ? "is-invalid"
                          : ""
                      }`}
                      {...formik.getFieldProps("Employees")}
                    />
                    {formik.errors.Employees && formik.touched.Employees && (
                      <div className="invalid-feedback">
                        {formik.errors.Employees}
                      </div>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup>
                <Label
                  htmlFor="Location"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Location
                </Label>
                <Input
                  name="Location"
                  type="text"
                  className={`form-control ${
                    formik.errors.Location && formik.touched.Location
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("Location")}
                />
                {formik.errors.Location && formik.touched.Location && (
                  <div className="invalid-feedback">
                    {formik.errors.Location}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label
                  htmlFor="email"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Email
                </Label>
                <Input
                  name="email"
                  type="email"
                  className={`form-control ${
                    formik.errors.email && formik.touched.email
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("email")}
                />
                {formik.errors.email && formik.touched.email && (
                  <div className="invalid-feedback">{formik.errors.email}</div>
                )}
              </FormGroup>

              <FormGroup>
                <Label
                  htmlFor="Website"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Website
                </Label>
                <Input
                  name="Website"
                  type="url"
                  className={`form-control ${
                    formik.errors.Website && formik.touched.Website
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("Website")}
                />
                {formik.errors.Website && formik.touched.Website && (
                  <div className="invalid-feedback">
                    {formik.errors.Website}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label
                  htmlFor="Established"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  Established Date
                </Label>
                <Input
                  name="Established"
                  type="date"
                  className={`form-control ${
                    formik.errors.Established && formik.touched.Established
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("Established")}
                />
                {formik.errors.Established && formik.touched.Established && (
                  <div className="invalid-feedback">
                    {formik.errors.Established}
                  </div>
                )}
              </FormGroup>

              <FormGroup>
                <Label
                  htmlFor="About_Company"
                  style={{
                    fontSize: "14px",
                    fontWeight: "400",
                    fontFamily: "poppins",
                  }}
                >
                  About Company
                </Label>
                <Input
                  name="About_Company"
                  type="textarea"
                  className={`form-control ${
                    formik.errors.About_Company && formik.touched.About_Company
                      ? "is-invalid"
                      : ""
                  }`}
                  {...formik.getFieldProps("About_Company")}
                />
                {formik.errors.About_Company &&
                  formik.touched.About_Company && (
                    <div className="invalid-feedback">
                      {formik.errors.About_Company}
                    </div>
                  )}
              </FormGroup>

              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="Company_Location.Longitude"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      Longitude
                    </Label>
                    <Input
                      name="Company_Location.Longitude"
                      type="number"
                      step="any"
                      className={`form-control ${
                        formik.errors.Company_Location?.Longitude &&
                        formik.touched.Company_Location?.Longitude
                          ? "is-invalid"
                          : ""
                      }`}
                      value={formik.values.Company_Location.Longitude}
                      onChange={(e) =>
                        formik.setFieldValue("Company_Location", {
                          ...formik.values.Company_Location,
                          Longitude: e.target.value,
                        })
                      }
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.Company_Location?.Longitude &&
                      formik.touched.Company_Location?.Longitude && (
                        <div className="invalid-feedback">
                          {formik.errors.Company_Location.Longitude}
                        </div>
                      )}
                  </FormGroup>
                </Col>
                <Col lg={6}>
                  <FormGroup>
                    <Label
                      htmlFor="Company_Location.Latitude"
                      style={{
                        fontSize: "14px",
                        fontWeight: "400",
                        fontFamily: "poppins",
                      }}
                    >
                      Latitude
                    </Label>
                    <Input
                      name="Company_Location.Latitude"
                      type="number"
                      step="any"
                      className={`form-control ${
                        formik.errors.Company_Location?.Latitude &&
                        formik.touched.Company_Location?.Latitude
                          ? "is-invalid"
                          : ""
                      }`}
                      value={formik.values.Company_Location.Latitude}
                      onChange={(e) =>
                        formik.setFieldValue("Company_Location", {
                          ...formik.values.Company_Location,
                          Latitude: e.target.value,
                        })
                      }
                      onBlur={formik.handleBlur}
                    />
                    {formik.errors.Company_Location?.Latitude &&
                      formik.touched.Company_Location?.Latitude && (
                        <div className="invalid-feedback">
                          {formik.errors.Company_Location.Latitude}
                        </div>
                      )}
                  </FormGroup>
                </Col>
              </Row>

              <FormGroup className="d-flex align-items-center flex-wrap gap-3">
                <div>
                  <Label
                    htmlFor="profileImage"
                    style={{
                      fontSize: "14px",
                      fontWeight: "400",
                      fontFamily: "poppins",
                      marginBottom: "8px",
                    }}
                  >
                    Profile Image
                  </Label>
                  <div className="custom-file-upload">
                    <Input
                      id="profileImage"
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      style={{ display: "none" }}
                      aria-label="Upload profile image"
                    />
                    <Button
                      color="primary"
                      size="sm"
                      style={{
                        fontFamily: "poppins",
                        padding: "8px 16px",
                        borderRadius: "8px",
                        transition:
                          "background-color 0.3s ease, transform 0.2s ease",
                      }}
                      className="custom-upload-button"
                      onClick={() =>
                        document.getElementById("profileImage").click()
                      }
                    >
                      <i className="ri-upload-2-line me-2"></i> Upload Image
                    </Button>
                  </div>
                </div>
                {profileImage ? (
                  <div className="image-preview-container mt-2">
                    <img
                      src={profileImage}
                      alt="Profile Preview"
                      style={{
                        width: "100px",
                        height: "100px",
                        borderRadius: "50%",
                        objectFit: "cover",
                        border: "2px solid #e9ecef",
                        boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                      }}
                    />
                  </div>
                ) : (
                  <div
                    className="image-placeholder mt-2 d-flex align-items-center justify-content-center"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "50%",
                      backgroundColor: "#f1f3f5",
                      border: "2px dashed #ced4da",
                      color: "#6c757d",
                      fontFamily: "poppins",
                      fontSize: "14px",
                      textAlign: "center",
                    }}
                  >
                    No Image
                  </div>
                )}
              </FormGroup>

              <div className="mt-4 text-end">
                <Button
                  type="submit"
                  color={submitted ? "success" : "primary"}
                  disabled={formik.isSubmitting || !userId}
                  style={{ fontFamily: "poppins" }}
                >
                  {formik.isSubmitting
                    ? "Saving..."
                    : submitted
                    ? "Saved"
                    : "Save Changes"}
                </Button>
              </div>
            </Form>
          </CardBody>
        </Card>

        <Modal isOpen={isCropModalOpen} toggle={toggleCropModal} centered>
          <ModalBody>
            <Cropper
              src={cropImage}
              style={{ height: 300, width: "100%" }}
              aspectRatio={1}
              guides={false}
              ref={cropperRef}
            />
          </ModalBody>
          <ModalFooter>
            <Button
              color="primary"
              onClick={handleCrop}
              style={{ fontFamily: "poppins" }}
            >
              Crop & Save
            </Button>
            <Button
              color="secondary"
              onClick={toggleCropModal}
              style={{ fontFamily: "poppins" }}
            >
              Cancel
            </Button>
          </ModalFooter>
        </Modal>
      </TabPane>
    </React.Fragment>
  );
};

export default BuyerForm;
